import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from '../DataTable/DataTable';
import recruiterActivityReports from "../../apis/requisitionReports/recruiterActivityReport";
import configJSON from "../../Config.json";
import "./requisitionReports.css";

import { NotificationManager } from "react-notifications";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

const stylesSX = {
  odd: {
    background: '#F6F6F6 !important',
  },
  even: {
    background: '#ffffff !important',
  },
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }),
  waitingText: {
    marginLeft: '5px',
  },
  clickable: {
    cursor: 'pointer',
  },
  nonClickable: {
    cursor: 'not-allowed',
  },
};

const RecruitersActivity = (props) => {
    const {
        userType,
        userIdType,
        userRecruitmentpartnerId,
        triggerAction,
        resetTrigger,
        startSelectedDate,
        endSelectedDate,
        additionalFilterData
    } = props;

    const history = useNavigate();

    const [takeNumberOfResult, setTakeNumberOfResult] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [isSearching, setIsSearching] = useState(true);
    const [totalResult, setTotalResult] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [dataResult, setDataResult] = useState([]);

    // Track previous date and filter values to detect changes using refs
    const prevStartDateRef = useRef(null);
    const prevEndDateRef = useRef(null);
    const prevFilterDataRef = useRef({});

    // Memoize filter data to prevent unnecessary re-renders
    const memoizedFilterData = useMemo(() => ({
        clientName: additionalFilterData?.clientName,
        divisionId: additionalFilterData?.divisionId,
    }), [additionalFilterData?.clientName, additionalFilterData?.divisionId]);

    // Handle clickable cell navigation
    const handleCellClick = useCallback((rowData, field, code) => {
        history('/requisition-report-details', {
            state: {
                recId: rowData.recruiterDetails.id,
                field: code || "",
                startDate: startSelectedDate.toISOString(),
                endDate: endSelectedDate.toISOString()
            }
        });
    }, [history, startSelectedDate, endSelectedDate]);

    // Define columns for the DataTable
    const columns = [
        {
            accessorKey: "recruiterDetails.displayName",
            header: "Recruiter Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "recruiterDetails.email",
            header: "Email ID",
            width: "300px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "assignedRequisitionsCount",
            header: "Requisition Assigned",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "assignedRequisitionsCount", "") : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "covered",
            header: "Covered",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.requisitionStatusCodes?.OPENSUBSTATUS.COVERED;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "covered", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "uncovered",
            header: "Uncovered",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.requisitionStatusCodes?.OPENSUBSTATUS.UNCOVERED;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "uncovered", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "submitted",
            header: "Submitted",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.SUBMITTED;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "submitted", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "clientDeclined",
            header: "Client Declined",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.CLIENT_DECLINED;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "clientDeclined", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "interviewScheduled",
            header: "L1 Interview",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.L1_Interview;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "interviewScheduled", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "interviewScheduled_L2",
            header: "L2 Interview",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.L2_Interview;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "interviewScheduled_L2", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "interviewScheduled_L3",
            header: "L3 Interview",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.L3_Interview;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "interviewScheduled_L3", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "selected",
            header: "Selected",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.SELECTED;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "selected", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "candidateDeclined",
            header: "Candidate Declined",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.CANDIDATE_DECLINED;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "candidateDeclined", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "started",
            header: "Started",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.STARTED;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "started", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: "dropout",
            header: "Dropout",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ row, getValue }) => {
                const value = parseInt(getValue());
                const code = configJSON?.candidateRequisitionStatusCode?.DROPOUT;
                return (
                    <div
                        onClick={value > 0 ? () => handleCellClick(row.original, "dropout", code) : () => {}}
                        style={{
                            cursor: value > 0 ? 'pointer' : 'not-allowed',
                            color: value > 0 ? 'blue' : 'inherit'
                        }}
                    >
                        {value}
                    </div>
                );
            },
        },
    ];

    // Format date function
    const formatDateOnly = useCallback((dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // Excel export function
    const callApiToGenerateExcel = useCallback(async () => {
        if (!startSelectedDate || !endSelectedDate) {
            NotificationManager.error("Please enter a valid date", "Error", 10000);
            return;
        }

        setIsExporting(true);

        try {
            let exportArr = [];
            const maxSize = configJSON.MAX_SIZE ;
            const end = Math.ceil(totalResult / maxSize);
            let pageStart = 1;
            // Fetch all data in chunks
            for (let start = 0; start < end; start++) {
                const payload = {
                    orgId: userRecruitmentpartnerId,
                    page: pageStart,
                    size: maxSize,
                    formattedDatestart: formatDateOnly(startSelectedDate),
                    formattedDateend: formatDateOnly(endSelectedDate),
                    isExport: true,
                    userId: userIdType,
                    role: userType,
                    additionalFilter: {
                        clientName: memoizedFilterData.clientName,
                        divisionId: memoizedFilterData.divisionId,
                    }
                };

                const response = await recruiterActivityReports(payload);

                response.data.forEach(element => {
                    exportArr.push({
                        "Recruiter Name": element.recruiterDetails.displayName,
                        "Email ID": element.recruiterDetails.email,
                        "Requisition Assigned": element.assignedRequisitionsCount,
                        "Covered": element.covered,
                        "Uncovered": element.uncovered,
                        "Submitted": element.submitted,
                        "Client Declined": element.clientDeclined,
                        "L1 Interview": element.interviewScheduled,
                        "L2 Interview": element.interviewScheduled_L2,
                        "L3 Interview": element.interviewScheduled_L3,
                        "Selected": element.selected,
                        "Candidate Declined": element.candidateDeclined,
                        "Started": element.started,
                        "Dropout": element.dropout,
                    });
                });
                pageStart += maxSize;
            }

            if (exportArr.length === 0) {
                NotificationManager.error("No data to export", "Error", 10000);
                return;
            }
            // Lazy load XLSX only when needed for export
            const { utils, writeFile } = await import("xlsx");
            // Create single sheet workbook
            const wb = utils.book_new();
            const ws = utils.json_to_sheet(exportArr);
            utils.book_append_sheet(wb, ws, "Recruiter Activity");

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            writeFile(wb, `RecruiterActivity_${timestamp}.xlsx`);

        } catch (error) {
            console.error("Export failed:", error);
            NotificationManager.error("Failed to generate report", "Error", 10000);
        } finally {
            setIsExporting(false);
        }
    }, [totalResult, userRecruitmentpartnerId, startSelectedDate, endSelectedDate, userIdType, userType, memoizedFilterData, formatDateOnly]);

    // Search function without debounce
    const performSearch = useCallback(async (
        userRecruitmentpartnerId,
        pageNumber,
        takeNumberOfResult,
        startSelectedDate,
        endSelectedDate,
        userId,
        filterData
    ) => {
        if (!startSelectedDate || !endSelectedDate) {
            NotificationManager.error("Please enter a valid date", "Error", 10000);
            return;
        }

        setIsSearching(true);
        let payload = {
            orgId: userRecruitmentpartnerId,
            page: pageNumber,
            size: takeNumberOfResult,
            formattedDatestart: formatDateOnly(startSelectedDate),
            formattedDateend: formatDateOnly(endSelectedDate),
            isExport: false,
            userId: userId,
            role: userType,
            additionalFilter: {
                clientName: filterData.clientName,
                divisionId: filterData.divisionId,
            }
        };

        try {
            const search = await recruiterActivityReports(payload);
            if (search?.data?.length > 0) {
                setDataResult(search.data);
                setTotalResult(search.totalCount[0]?.totalCount || 0);
            } else {
                setDataResult([]);
                setTotalResult(0);
            }
        } catch (error) {
            console.error("Error fetching recruiter activity reports:", error);
            setDataResult([]);
            setTotalResult(0);
            NotificationManager.error("Error fetching data", "Error", 10000);
        } finally {
            setIsSearching(false);
        }
    }, [formatDateOnly, userType]);

    // Handle page change
    const handlePageChange = (newPage, newPageSize) => {
        setPageNumber(newPage);
        if (newPageSize && newPageSize !== takeNumberOfResult) {
            setTakeNumberOfResult(newPageSize);
        }
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        setTakeNumberOfResult(newPageSize);
        setPageNumber(1);
    };

    

    useEffect(() => {
        if (triggerAction) {
            callApiToGenerateExcel();
            resetTrigger();
        }
    }, [triggerAction, resetTrigger, callApiToGenerateExcel]);

    // Check if dates or filters have changed
    useEffect(() => {
        // Check if dates have changed
        const datesChanged =
            prevStartDateRef.current !== startSelectedDate ||
            prevEndDateRef.current !== endSelectedDate;

        // Check if filters have changed
        const filtersChanged = JSON.stringify(prevFilterDataRef.current) !== JSON.stringify(memoizedFilterData);

        // Reset to page 1 if dates or filters changed
        if (datesChanged || filtersChanged) {
            setPageNumber(1);
        }

        // Update previous values after checking
        prevStartDateRef.current = startSelectedDate;
        prevEndDateRef.current = endSelectedDate;
        prevFilterDataRef.current = { ...memoizedFilterData };
    }, [startSelectedDate, endSelectedDate, memoizedFilterData]);

    useEffect(() => {
        if (!startSelectedDate || !endSelectedDate) {
            NotificationManager.error("Please enter a valid date", "Error", 10000);
            return;
        }

        // When any dependency changes, schedule a search.
        setIsSearching(true);
        setDataResult([]);

        const timeoutId = setTimeout(() => {
            performSearch(
                userRecruitmentpartnerId,
                pageNumber,
                takeNumberOfResult,
                startSelectedDate,
                endSelectedDate,
                userIdType,
                memoizedFilterData
            );
        }, 1000); // 1000ms debounce delay

        // Cleanup cancels the pending search if dependencies change quickly.
        return () => clearTimeout(timeoutId);
    }, [
        pageNumber,
        takeNumberOfResult,
        startSelectedDate,
        endSelectedDate,
        memoizedFilterData,
        userRecruitmentpartnerId,
        userIdType,
        performSearch
    ]);

    return (
        <>
            <Backdrop sx={stylesSX.backdrop} open={isExporting}>
                <CircularProgress color="inherit" />
                <Typography sx={stylesSX.waitingText}>Please wait...</Typography>
            </Backdrop>

            <DataTable
                data={dataResult}
                columns={columns}
                isLoading={isSearching}
                totalRecords={totalResult}
                currentPage={pageNumber}
                pageSize={takeNumberOfResult}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                
                enableFiltering={false} 
                enablePagination={true}
                enableSorting={false} 
                pageSizeOptions={[10, 20, 50, 100]}
                isServerSide={true}
                className="kendo_reports_recruiterActivity"
                noDataText="No results found !"
            />
        </>
    );
};

export default RecruitersActivity;
